import { useEffect } from 'react';

const REVEAL_SELECTOR = '.js-reveal, .js-reveal-left, .js-reveal-right, .js-reveal-zoom, .js-reveal-img, .js-stagger';
const PARALLAX_SELECTOR = '.js-parallax';
const PARALLAX_SPEED = 0.18;
/** Longest reveal duration plus the largest stagger delay; after this the element drops its reveal transition. */
const SETTLE_MS = 2200;

/**
 * Scroll-driven motion for the whole site, mounted once by the Header.
 *
 * Reveal targets are tagged with `.js-reveal*` / `.js-stagger` classes and driven
 * through a `data-reveal` attribute (pending → in → done) so React re-renders never
 * clobber the state. Content that is already on screen at mount stays put to avoid
 * flashing SSR markup; anything inserted later (live collections, editor changes,
 * filter swaps) animates in.
 */
export function useSiteMotion() {
  useEffect(() => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const seen = new WeakSet<Element>();
    const timers = new Set<number>();

    const settle = (el: Element) => {
      el.setAttribute('data-reveal', 'in');
      const t = window.setTimeout(() => {
        timers.delete(t);
        el.setAttribute('data-reveal', 'done');
      }, SETTLE_MS);
      timers.add(t);
    };

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          io.unobserve(entry.target);
          settle(entry.target);
        }
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0 },
    );

    const track = (el: Element, initial: boolean) => {
      if (seen.has(el)) return;
      seen.add(el);
      if (initial) {
        const r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) {
          el.setAttribute('data-reveal', 'done');
          return;
        }
      }
      el.setAttribute('data-reveal', 'pending');
      io.observe(el);
    };

    document.querySelectorAll(REVEAL_SELECTOR).forEach((el) => track(el, true));

    // Parallax backgrounds
    let layers: HTMLElement[] = [];
    const collectLayers = () => {
      layers = Array.from(document.querySelectorAll<HTMLElement>(PARALLAX_SELECTOR));
    };
    let frame = 0;
    const paint = () => {
      frame = 0;
      const vh = window.innerHeight;
      for (const el of layers) {
        const host = el.closest('section') || el.parentElement;
        if (!host) continue;
        const r = host.getBoundingClientRect();
        if (r.bottom < -100 || r.top > vh + 100) continue;
        const max = Math.max(0, (el.offsetHeight - r.height) / 2 - 1);
        const raw = -(r.top + r.height / 2 - vh / 2) * PARALLAX_SPEED;
        const y = Math.max(-max, Math.min(max, raw));
        el.style.transform = `translate3d(0, ${y.toFixed(1)}px, 0)`;
      }
    };
    const schedule = () => {
      if (!frame) frame = window.requestAnimationFrame(paint);
    };
    collectLayers();
    paint();

    const mo = new MutationObserver((records) => {
      let layersChanged = false;
      for (const rec of records) {
        rec.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;
          if (node.matches(REVEAL_SELECTOR)) track(node, false);
          node.querySelectorAll(REVEAL_SELECTOR).forEach((el) => track(el, false));
          if (node.matches(PARALLAX_SELECTOR) || node.querySelector(PARALLAX_SELECTOR)) layersChanged = true;
        });
        if (rec.removedNodes.length) layersChanged = true;
      }
      if (layersChanged) {
        collectLayers();
        schedule();
      }
    });
    mo.observe(document.body, { childList: true, subtree: true });

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    return () => {
      io.disconnect();
      mo.disconnect();
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
      if (frame) window.cancelAnimationFrame(frame);
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, []);
}
